import {Catalog} from '../../../../common/types/common';
import {
    AttachmentCatalogsState,
    AttachmentDetail, AttachmentsForm,
    BureauInformationDataState,
    ContinueLoanApplicationState, DocsUI,
    GlobalApplicationDataState, LoanGroupedAttachmentsState,
    POSDataState, SendLoanApplicationBankState,
    POSDataViewState,
    PipefyDataState, PdfViewState
} from './types';

export interface LoanApplicationsItemState {
    applicantCompleteName: string;
    bankApplicationId: string;
    brand: Catalog;
    hash: string;
    model: Catalog;
    selected: boolean;
    stage: string;
    token: string;
    updatedAt: string;
    vehicleDescription: string;
    vehiclePrice: string;
    year: number;
    posGroupName: string;
    posGroupId: string;
    posGroupType: string;
    target: Catalog;
    lost?: boolean;
}

export interface LoanSearch {
    searchQuery: string;
    showFilterBox: boolean;
    // true while the search query was set by an automated navigation (e.g. the Aprobaciones "Editar"
    // deep link) rather than typed by the user; the input stays disabled until "limpiar" is pressed.
    disabled: boolean;
}

export interface LoanApplicationListViewState {
    hasMoreLoanApplications: boolean;
    lastPage: number;
}

export interface LoanApplicationDetailViewState {
    showLoading: boolean;
}

export interface LoanApplicationsState {
    byId: { [key: string]: LoanApplicationsItemState };
}

export interface CommonLoanFilters {
    financialEntityType: Catalog
    target: Catalog
    loanApplicationType: Catalog
    channel: Catalog
    hasFilters: boolean
}

export interface CommonLoanCatalogs {
    financialEntityTypes: Catalog[]
    targets: Catalog[]
    loanApplicationTypes: Catalog[]
    channels: Catalog[]
    houseHoldMeasures: Catalog[]
    houseHoldTimes: Catalog[]
    openingCommissions: Catalog[]
    installationGpsStatus: Catalog[]
    crHouseTypes: Catalog[]
    crDocumentTypes: Catalog[]
    crBusinessTypes: Catalog[]
    crGender: Catalog[]
    crCivilStatus: Catalog[]
    crStudiesLevel: Catalog[]
    coCivilStatus: Catalog[]
    coStudiesLevel: Catalog[]
    coDocumentTypes: Catalog[]
    coLowerPercentage: Catalog[]
    countriesWithNationalities: Catalog[]
    assemblyCountry: Catalog[]
    loseApplicationReasons: Catalog[]
}

export interface CommonLoanApplicationState {
    loanApplications: LoanApplicationsState
    loanSearch: LoanSearch
    loanApplicationListView: LoanApplicationListViewState
    loanApplicationDetailView: LoanApplicationDetailViewState
    filters: CommonLoanFilters
    catalogs: CommonLoanCatalogs
    bureauInformation: BureauInformationDataState
    continueLoanApplication: ContinueLoanApplicationState
    sendLoanApplicationBank: SendLoanApplicationBankState
    globalApplication: GlobalApplicationDataState
    pos: CommonPOSState
    docs: DocsState,
    pipefy: PipefyDataState
    pipefyCreditas: PipefyDataState
    pipefyRapidauto: PipefyDataState
    pdfPreview: PdfViewState
    closingAdviser: Catalog
}

export interface CommonPOSState {
    posData: POSDataState
    ui: POSDataViewState
    catalogs: POSCatalogsState
}

export interface POSCatalogsState {
    groups: Catalog[]
}

export interface LoanFiltersState {
    advisers: Catalog[],
    pipelines: Catalog[],
    stages: Catalog[],
    adviser: Catalog,
    pipeline: Catalog,
    stage: Catalog,
    hasFilters: boolean;
}

export class PreviewFieldError {
    name: string;
    hasError: boolean;

    constructor(name: string,
                hasError: boolean) {
        this.name = name;
        this.hasError = hasError;
    }
}

export type PreviewFieldErrorGeneric<T> = {
    [key in keyof T]: {
        name: string
        hasError: boolean
    }
};

export interface PreviewData<T> {
    sectionTitle: string;
    sectionMenu: string;
    fields: T;
}

export interface PreviewLoanApplicationPersonalDataFieldErrors {
    hasFirstNameError: PreviewFieldError;
    hasLastNameError: PreviewFieldError;
    hasSecondLastNameError: PreviewFieldError;
    hasBirthDateError: PreviewFieldError;
    hasDocNumberError: PreviewFieldError;
    hasCellPhoneError: PreviewFieldError;
    hasEmailError: PreviewFieldError;
}

export class PersonalDataViewFieldErrorState {
    firstNameFieldError: string;
    lastNameFieldError: string;
    secondLastNameFieldError: string;
    birthDateFieldError: string;
    docNumberFieldError: string;
    cellPhoneFieldError: string;
    emailFieldError: string;

    constructor(firstNameFieldError: string,
                lastNameFieldError: string,
                secondLastNameFieldError: string,
                birthDateFieldError: string,
                docNumberFieldError: string,
                cellPhoneFieldError: string,
                emailFieldError: string) {
        this.firstNameFieldError = firstNameFieldError;
        this.lastNameFieldError = lastNameFieldError;
        this.secondLastNameFieldError = secondLastNameFieldError;
        this.birthDateFieldError = birthDateFieldError;
        this.docNumberFieldError = docNumberFieldError;
        this.cellPhoneFieldError = cellPhoneFieldError;
        this.emailFieldError = emailFieldError;
    }
}

export interface PreviewLoanApplicationHomeDataFieldErrors {
    hasAddressError: PreviewFieldError;
    hasExtNumberError: PreviewFieldError;
    hasPostalCodeError: PreviewFieldError;
    hasColonyError: PreviewFieldError;
    hasPhoneError: PreviewFieldError;
}

export class HomeDataViewFieldErrorState {
    addressFieldError: string;
    extNumberFieldError: string;
    intNumberFieldError?: string;
    postalCodeFieldError: string;
    stateFieldError: string;
    cityFieldError?: string;
    municipalityFieldError: string;
    colonyFieldError: string;
    phoneFieldError?: string;
    houseHoldMeasureFieldError: string;
    houseHoldTimeFieldError: string;

    constructor(addressFieldError: string,
                extNumberFieldError: string,
                intNumberFieldError: string,
                postalCodeFieldError: string,
                stateFieldError: string,
                cityFieldError: string,
                municipalityFieldError: string,
                colonyFieldError: string,
                phoneFieldError: string,
                houseHoldFieldError: string = null,
                houseHoldTimeFieldError: string = null) {

        this.addressFieldError = addressFieldError;
        this.extNumberFieldError = extNumberFieldError;
        this.intNumberFieldError = intNumberFieldError;
        this.postalCodeFieldError = postalCodeFieldError;
        this.stateFieldError = stateFieldError;
        this.cityFieldError = cityFieldError;
        this.municipalityFieldError = municipalityFieldError;
        this.colonyFieldError = colonyFieldError;
        this.phoneFieldError = phoneFieldError;
        this.houseHoldMeasureFieldError = houseHoldFieldError;
        this.houseHoldTimeFieldError = houseHoldTimeFieldError;
    }
}

export interface PreviewLoanApplicationWorkDataFieldErrors {
    hasWorkplaceNameError: PreviewFieldError;
    hasPositionError: PreviewFieldError;
    hasWorkingSinceError: PreviewFieldError;
    hasAddressError: PreviewFieldError;
    hasExtNumberError: PreviewFieldError;
    hasPostalCodeError: PreviewFieldError;
    hasColonyError: PreviewFieldError;
    hasWorkplacePhoneError: PreviewFieldError;
    hasMonthlyIncomesError: PreviewFieldError;
}

export class WorkDataViewFieldErrorState {
    workplaceNameFieldError: string;
    positionFieldError: string;
    workingSinceFieldError?: string;
    addressFieldError: string;
    extNumberFieldError: string;
    postalCodeFieldError: string;
    stateFieldError: string;
    cityFieldError: string;
    municipalityFieldError: string;
    colonyFieldError: string;
    workplacePhoneFieldError?: string;
    monthlyIncomesFieldError?: string;

    constructor(workplaceNameFieldError: string = null,
                positionFieldError: string = null,
                workingSinceFieldError: string = null,
                addressFieldError: string = null,
                extNumberFieldError: string = null,
                postalCodeFieldError: string = null,
                stateFieldError: string = null,
                cityFieldError: string = null,
                municipalityFieldError: string = null,
                colonyFieldError: string = null,
                workplacePhoneFieldError: string = null,
                monthlyIncomesFieldError: string = null) {

        this.workplaceNameFieldError = workplaceNameFieldError;
        this.positionFieldError = positionFieldError;
        this.workingSinceFieldError = workingSinceFieldError;
        this.addressFieldError = addressFieldError;
        this.extNumberFieldError = extNumberFieldError;
        this.postalCodeFieldError = postalCodeFieldError;
        this.stateFieldError = stateFieldError;
        this.cityFieldError = cityFieldError;
        this.municipalityFieldError = municipalityFieldError;
        this.colonyFieldError = colonyFieldError;
        this.workplacePhoneFieldError = workplacePhoneFieldError;
        this.monthlyIncomesFieldError = monthlyIncomesFieldError;
    }
}

export interface PreviewLoanApplicationReferencesDataFieldErrors {
    hasReference1CompleteNameError: PreviewFieldError;
    hasReference1PhoneError: PreviewFieldError;
    hasReference2CompleteNameError: PreviewFieldError;
    hasReference2PhoneError: PreviewFieldError;
    hasReference3CompleteNameError: PreviewFieldError;
    hasReference3PhoneError: PreviewFieldError;
}

export class ReferenceViewFieldErrorState {

    referenceFieldError1: ReferenceFieldError;
    referenceFieldError2: ReferenceFieldError;
    referenceFieldError3: ReferenceFieldError;

    constructor(referenceFieldError1: ReferenceFieldError =
                    {completeNameError: null, phoneError: null},
                referenceFieldError2: ReferenceFieldError =
                    {completeNameError: null, phoneError: null},
                referenceFieldError3: ReferenceFieldError =
                    {completeNameError: null, phoneError: null}) {
        this.referenceFieldError1 = referenceFieldError1;
        this.referenceFieldError2 = referenceFieldError2;
        this.referenceFieldError3 = referenceFieldError3;
    }
}

export interface ReferenceFieldError {
    completeNameError: string;
    phoneError: string;
}

export class InsuranceViewFieldErrorState {
    isFinancedFieldError: string;
    insuranceAmountFieldError: string;
    insuranceCarrierSelectedFieldError: string;

    constructor(isFinancedFieldError: string = null,
                insuranceAmountFieldError: string = null,
                insuranceCarrierSelectedFieldError: string = null) {
        this.isFinancedFieldError = isFinancedFieldError;
        this.insuranceAmountFieldError = insuranceAmountFieldError;
        this.insuranceCarrierSelectedFieldError = insuranceCarrierSelectedFieldError;
    }
}

export class VehicleDataViewFieldErrorState {
    yearFieldError: string;
    brandFieldError: string;
    modelFieldError: string;
    versionFieldError: string;
    mileageFieldError?: string;
    vehicleIdFieldError?: string;
    assemblyCountryFieldError?: string;

    constructor(yearFieldError: string = null,
                brandFieldError: string = null,
                modelFieldError: string = null,
                versionFieldError: string = null,
                mileageFieldError: string = null,
                vehicleIdFieldError: string = null,
                assemblyCountryFieldError: string = null,
                ) {

        this.yearFieldError = yearFieldError;
        this.brandFieldError = brandFieldError;
        this.modelFieldError = modelFieldError;
        this.versionFieldError = versionFieldError;
        this.mileageFieldError = mileageFieldError;
        this.vehicleIdFieldError = vehicleIdFieldError;
        this.assemblyCountryFieldError = assemblyCountryFieldError;
    }
}

export class PersonalInterviewDataViewFieldErrorState {
    thirdPartyNameFieldError: string;
    isPublicEmployeeFieldError: string;
    resourceTypeFieldError: string;
    sourceResourceTypeFieldError: string;

    constructor(thirdPartyNameFieldError: string = null,
                isPublicEmployeeFieldError: string = null,
                resourceTypeFieldError: string = null,
                sourceResourceTypeFieldError: string = null) {

        this.thirdPartyNameFieldError = thirdPartyNameFieldError;
        this.isPublicEmployeeFieldError = isPublicEmployeeFieldError;
        this.resourceTypeFieldError = resourceTypeFieldError;
        this.sourceResourceTypeFieldError = sourceResourceTypeFieldError;
    }
}

export class CreditConditionViewFieldErrorState {
    vehiclePriceFieldError: string;
    downPaymentFieldError: string;
    termSelectedFieldError: string;
    openingCommissionFieldError?: string;

    constructor(vehiclePriceFieldError: string = null,
                downPaymentFieldError: string = null,
                termSelectedFieldError: string = null,
                openingCommissionFieldError: string = null) {
        this.vehiclePriceFieldError = vehiclePriceFieldError;
        this.downPaymentFieldError = downPaymentFieldError;
        this.termSelectedFieldError = termSelectedFieldError;
        this.openingCommissionFieldError = openingCommissionFieldError;
    }
}

export class POSViewFieldErrorState {

    posGroupFieldError: string;

    constructor(posGroupFieldError: string = null) {
        this.posGroupFieldError = posGroupFieldError;
    }
}

export interface PreviewLoanApplicationVehicleData {
    sectionTitle: string;
    sectionMenu: string;
    fields: PreviewLoanApplicationVehicleDataFieldErrors;
}

export interface PreviewLoanApplicationVehicleDataFieldErrors {
    hasYearError: PreviewFieldError;
    hasBrandError: PreviewFieldError;
    hasModelError: PreviewFieldError;
    hasVersionError: PreviewFieldError;
}

export interface PreviewLoanApplicationCreditConditionsData {
    sectionTitle: string;
    sectionMenu: string;
    fields: PreviewLoanApplicationCreditConditionsDataFieldErrors;
}

export interface PreviewLoanApplicationCreditConditionsDataFieldErrors {
    hasPriceError: PreviewFieldError;
    hasDownPaymentError: PreviewFieldError;
    hasTermError: PreviewFieldError;
}

export interface PreviewLoanApplicationInsuranceData {
    sectionTitle: string;
    sectionMenu: string;
    fields: PreviewLoanApplicationInsuranceDataFieldErrors;
}

export interface PreviewLoanApplicationInsuranceDataFieldErrors {
    hasInsuranceAmountError: PreviewFieldError;
    hasTargetError: PreviewFieldError;
}

export interface PreviewQuotePersonalDataFieldErrors {
    hasFirstNameError: PreviewFieldError;
    hasLastNameError: PreviewFieldError;
    hasSecondLastNameError: PreviewFieldError;
}

export interface PreviewQuoteVehicleDataFieldErrors {
    hasYearError: PreviewFieldError;
    hasBrandError: PreviewFieldError;
    hasModelError: PreviewFieldError;
    hasVersionError: PreviewFieldError;
}

export interface PreviewQuoteCreditConditionsDataFieldErrors {
    hasPriceError: PreviewFieldError;
    hasDownPaymentError: PreviewFieldError;
    hasTermError: PreviewFieldError;
    hasPromotionError: PreviewFieldError;
}

export interface PreviewQuoteMechanicalDataFieldErrors {
    hasMechanicalWarrantyError: PreviewFieldError;
}

export interface PreviewInsurancePersonalDataFieldErrors {
    hasFirstNameError: PreviewFieldError;
    hasLastNameError: PreviewFieldError;
    hasSecondLastNameError: PreviewFieldError;
    hasDocNumberError: PreviewFieldError;
    hasCellPhoneError: PreviewFieldError;
    hasEmailError: PreviewFieldError;
}

export interface PreviewInsuranceHomeDataFieldErrors {
    hasAddressError: PreviewFieldError;
    hasPostalCodeError: PreviewFieldError;
}

export interface PreviewInsuranceVehicleDataFieldErrors {
    hasYearError: PreviewFieldError;
    hasBrandError: PreviewFieldError;
    hasModelError: PreviewFieldError;
    hasVersionError: PreviewFieldError;
    hasVinError: PreviewFieldError;
}

export interface PreviewInsuranceCreditConditionsDataFieldErrors {
    hasPriceError: PreviewFieldError;
}

export interface PreviewInsuranceInsuranceDataFieldErrors {
    hasInsuredAmountError: PreviewFieldError;
    hasStartDateError: PreviewFieldError;
    hasUsualDriverError: PreviewFieldError;
}

export interface SimpleMenuState {
    id: string,
    label: string,
    hovered: boolean,
    selected: boolean
}

export interface DocsState {
    menu: SimpleMenuState[];
    catalogs: AttachmentCatalogsState
    attachmentsForm: AttachmentsForm
    attachmentDetail: AttachmentDetail
    ui: DocsUI
    groupAttachments: LoanGroupedAttachmentsState[];
}

export interface LastWorkDataViewFieldErrorState {
    workplaceNameError: string
    employmentBusinessTypeError: string
    yearsOfWorkMeasureError: string
    yearsOfWorkTimeError: string
    workingUpError: string
}

export class RecordsDataViewFieldErrorState {
    statusFieldError: string;
    dateSentFieldError: string;
    dateReceivedFieldError: string;
    dateOwnerChangedFieldError: string;
    commentsFieldError: string;
    dateDisbursementDelayedFieldError: string;

    constructor(statusFieldError: string,
                dateSentFieldError: string,
                dateReceivedFieldError: string,
                commentsFieldError: string,
                dateOwnerChangedFieldError: string,
                dateDisbursementDelayedFieldError: string) {
        this.statusFieldError = statusFieldError;
        this.dateSentFieldError = dateSentFieldError;
        this.dateReceivedFieldError = dateReceivedFieldError;
        this.commentsFieldError = commentsFieldError;
        this.dateOwnerChangedFieldError = dateOwnerChangedFieldError;
        this.dateDisbursementDelayedFieldError = dateDisbursementDelayedFieldError;
    }
}

export class GiftCardsDataViewFieldErrorState {
    fieldErrors: GiftCardDataViewFieldErrorState[];

    constructor(fieldErrors: GiftCardDataViewFieldErrorState[]) {
        this.fieldErrors = fieldErrors;
    }
}

export class GiftCardDataViewFieldErrorState {
    beneficiaryFieldError: string;
    amountFieldError: string;

    constructor(beneficiaryFieldError: string,
                amountFieldError: string) {
        this.beneficiaryFieldError = beneficiaryFieldError;
        this.amountFieldError = amountFieldError;
    }
}
export class ClosingBankViewFieldErrorState {
    coppelFieldError: string;
    creditasFieldError: string;
    rapidautoFieldError: string;

    constructor(coppelFieldError: string,
                creditasFieldError: string,
                rapidautoFieldError: string) {
        this.coppelFieldError = coppelFieldError;
        this.creditasFieldError = creditasFieldError;
        this.rapidautoFieldError = rapidautoFieldError;
    }
}

export class ApprovalConditionsViewFieldErrorState {
    approvedDownPaymentFieldError: string;

    constructor(approvedDownPaymentFieldError: string) {
        this.approvedDownPaymentFieldError = approvedDownPaymentFieldError;
    }
}
